import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// iter122 — Register the offline short-circuit on the DEFAULT axios
// instance too, so modules like i18n.js / waAuth.js / featureFlags.js
// that import raw `axios` (not the `api` instance) also benefit. On iOS
// with Cellular Data turned off, ANY queued network request can raise
// the native "Cellular Data is Turned Off" dialog — this guard ensures
// no axios call ever reaches the network when navigator.onLine===false.
const offlineGuard = (config) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const err = new Error("offline");
    err.code = "ERR_OFFLINE";
    err.config = config;
    return Promise.reject(err);
  }
  return config;
};
axios.interceptors.request.use(offlineGuard);

// Admin Preview Mode — when admin is previewing a business/vendor, inject
// the corresponding `?as_business_id=…` / `?as_vendor_id=…` query param into
// every dashboard request so backend resolvers can load the right resource
// without breaking owner-bound endpoints.
const PREVIEW_LS_KEY = "habano.admin_preview";
function readPreview() {
  try { return JSON.parse(localStorage.getItem(PREVIEW_LS_KEY) || "null"); } catch { return null; }
}

api.interceptors.request.use((config) => {
  // iter122 — Global offline short-circuit. When the device reports
  // offline, abort the request before it touches the network so iOS
  // never raises its native "Cellular Data is Turned Off" dialog. Pages
  // already handle promise rejections via their existing `.catch`
  // fallbacks (cache reads, empty states, etc.).
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const err = new Error("offline");
    err.code = "ERR_OFFLINE";
    err.config = config;
    return Promise.reject(err);
  }
  const preview = readPreview();
  if (!preview || !preview.resource_id) return config;
  const url = config.url || "";
  const key = preview.account_type === "business" ? "as_business_id"
            : preview.account_type === "vendor" ? "as_vendor_id"
            : null;
  if (!key) return config;
  // Only inject for the matching dashboard surfaces
  const isBiz = preview.account_type === "business" && url.startsWith("/business/");
  const isVen = preview.account_type === "vendor" && url.startsWith("/vendor/");
  if (!isBiz && !isVen) return config;
  config.params = { ...(config.params || {}), [key]: preview.resource_id };
  return config;
});

/**
 * iter191 — Fix React error #31 ("Objects are not valid as a React
 * child").
 *
 * FastAPI returns validation errors (HTTP 422) with `detail` as a LIST
 * of Pydantic error objects like:
 *   [{"type":"missing","loc":["body","whatsapp"],"msg":"Field required",
 *     "input":{...},"ctx":{...},"url":"..."}, ...]
 *
 * Many toast handlers do `toast.error(e.response?.data?.detail || ...)`.
 * When `detail` is a raw object/array, React tries to render it as a
 * child and throws #31. This response interceptor normalizes `detail`
 * into a plain string BEFORE the axios promise rejects, so every
 * downstream `.catch` sees a safe scalar.
 *
 * Order preserved and messages joined with `; ` so multi-field errors
 * remain informative ("whatsapp: Field required; name: too short").
 */
function normalizeErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e === "object") {
          const msg = e.msg || e.message || "Invalid value";
          const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(e);
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || detail.error || JSON.stringify(detail);
  }
  return String(detail);
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      if (err && err.response && err.response.data) {
        const d = err.response.data.detail;
        // iter200 — Preserve structured error payloads that carry a
        // machine-readable `code` field (e.g. {code:'vendor_not_found',
        // message_en:'...', message_es:'...', reapply_path:'...'}).
        // Downstream code branches on `code` and renders friendly UI.
        const isStructured = d && typeof d === "object" && !Array.isArray(d) && typeof d.code === "string";
        if (d != null && typeof d !== "string" && !isStructured) {
          err.response.data.detail = normalizeErrorDetail(d);
        }
      }
    } catch (_ignored) { void _ignored; /* never let the interceptor swallow the original error */ }
    return Promise.reject(err);
  }
);

export async function getMe() {
  try {
    const { data } = await api.get("/auth/me");
    return data;
  } catch {
    return null;
  }
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch {}
}
